export const COLOR_MODE_STORAGE_KEY = 'eateliers-color-mode';

export type ColorMode = 'light' | 'dark';
export type ResolvedColorMode = ColorMode;

export const COLOR_MODES: ColorMode[] = ['light', 'dark'];

export const isColorMode = (value: unknown): value is ColorMode =>
  value === 'light' || value === 'dark';

export const nextColorMode = (mode: ColorMode): ColorMode =>
  mode === 'light' ? 'dark' : 'light';

export const persistColorMode = (mode: ColorMode): void => {
  try {
    window.localStorage.setItem(COLOR_MODE_STORAGE_KEY, mode);
  } catch (error) {
    console.error('Failed to persist color mode', { error });
  }
};

export const getSystemColorMode = (): ResolvedColorMode => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'light';
  }
  const media = window.matchMedia('(prefers-color-scheme: dark)');
  return media?.matches ? 'dark' : 'light';
};

export const readStoredColorMode = (): ColorMode => {
  try {
    const stored = window.localStorage.getItem(COLOR_MODE_STORAGE_KEY);
    if (isColorMode(stored)) {
      return stored;
    }
    if (stored === 'system') {
      const migrated = getSystemColorMode();
      persistColorMode(migrated);
      return migrated;
    }
    return 'light';
  } catch (error) {
    console.error('Failed to read color mode', { error });
    return 'light';
  }
};

export const resolveColorMode = (mode: ColorMode): ResolvedColorMode => mode;

/** Public bride-facing routes stay on the light boutique theme. */
export const isPublicColorModePath = (pathname: string): boolean =>
  /^\/bookings\/[^/]+/.test(pathname) ||
  /^\/store\/[^/]+/.test(pathname) ||
  /^\/drops\//.test(pathname) ||
  /^\/catalogues\/[^/]+/.test(pathname);

export const shouldApplyDarkClass = (
  resolved: ResolvedColorMode,
  pathname: string
): boolean => resolved === 'dark' && !isPublicColorModePath(pathname);

export const applyColorModeClass = (resolved: ResolvedColorMode, pathname: string): void => {
  const root = document.documentElement;
  const isDark = shouldApplyDarkClass(resolved, pathname);
  root.classList.toggle('dark', isDark);
  root.style.colorScheme = isDark ? 'dark' : 'light';
};
